import { useId } from "react";
import { AlertCircle, ArrowDownUp, Inbox } from "lucide-react";
import DataTableCheckbox from "./DataTableCheckbox";
import { DefaultState, INTERACTIVE, formatPlain } from "./DataTableBody";
import { cx, getCellValue } from "./utils";

const ACTIONS_KEY = "__actions";

// Phones: the same rows as a list. The first column is the row's title, the actions menu sits
// beside it, and the other columns are label/value lines underneath, so nothing scrolls sideways.
export default function DataTableCards({
  columns,
  rows,
  getRowKey,
  loading,
  loadingRowCount,
  error,
  errorState,
  emptyState,
  selectable,
  selectedSet,
  isRowSelectable,
  onToggleRow,
  allSelected,
  someSelected,
  onToggleAll,
  selectAllDisabled,
  getRowAriaLabel,
  onRowClick,
  rowClassName,
  rowOffset,
  sort,
  onSortChange,
  labels,
}) {
  const actions = columns.find((c) => c.key === ACTIONS_KEY);
  const fields = columns.filter((c) => c !== actions);
  const [titleColumn, ...detailColumns] = fields;
  const sortable = columns.filter((c) => c.sortable);
  const uid = useId();

  const sortBar = (sortable.length > 0 || selectable) && (
    <div className="flex items-center gap-3 border-b border-app-primary px-3 py-2">
      {selectable && (
        <div className="flex items-center gap-2 text-xs font-medium text-app-secondary">
          <DataTableCheckbox
            id={`${uid}-all`}
            checked={allSelected}
            indeterminate={someSelected}
            disabled={selectAllDisabled}
            onChange={onToggleAll}
            aria-label={labels.selectAll}
          />
          <label htmlFor={`${uid}-all`}>All</label>
        </div>
      )}
      {sortable.length > 0 && (
        <label className="ml-auto flex min-w-0 items-center gap-1.5 text-xs text-app-muted">
          <ArrowDownUp size={14} aria-hidden="true" />
          <span className="sr-only">Sort by</span>
          <select
            value={sort ? `${sort.key}:${sort.direction}` : ""}
            onChange={(e) => {
              const [key, direction] = e.target.value.split(":");
              onSortChange(key ? { key, direction } : null);
            }}
            className="min-w-0 rounded-lg border border-app-primary bg-app-input py-1 pl-2 pr-7 text-app-secondary"
          >
            <option value="">Default order</option>
            {sortable.map((c) => [
              <option key={`${c.key}:asc`} value={`${c.key}:asc`}>{`${c.label} ↑`}</option>,
              <option key={`${c.key}:desc`} value={`${c.key}:desc`}>{`${c.label} ↓`}</option>,
            ])}
          </select>
        </label>
      )}
    </div>
  );

  if (loading) {
    return (
      <ul aria-busy="true" className="divide-y divide-[color:var(--border-primary)]">
        {Array.from({ length: Math.min(loadingRowCount, 4) }, (_, r) => (
          <li key={r} aria-hidden="true" className="space-y-2.5 p-3">
            <div className="h-4 w-1/2 animate-pulse rounded-full bg-app-hover" />
            <div className="h-3 w-3/4 animate-pulse rounded-full bg-app-hover" />
            <div className="h-3 w-2/3 animate-pulse rounded-full bg-app-hover" />
          </li>
        ))}
      </ul>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-12">
        {errorState || (
          <DefaultState icon={AlertCircle} tone="error" role="alert" message={typeof error === "string" ? error : labels.error} />
        )}
      </div>
    );
  }

  if (rows.length === 0) {
    return <div className="px-4 py-12">{emptyState || <DefaultState icon={Inbox} message={labels.empty} />}</div>;
  }

  const handleClick = (event, row, index) => {
    if (!event.currentTarget.contains(event.target)) return;
    const interactive = event.target.closest?.(INTERACTIVE);
    if (interactive && event.currentTarget.contains(interactive)) return;
    if (window.getSelection?.().toString()) return;
    onRowClick(row, index);
  };

  const cell = (column, row, index) => {
    const value = getCellValue(row, column);
    return column.render ? column.render(value, row, index) : formatPlain(value);
  };

  return (
    <>
      {sortBar}
      <ul className="divide-y divide-[color:var(--border-primary)]">
        {rows.map((row, i) => {
          const index = rowOffset + i;
          const key = getRowKey(row);
          const selected = selectable && selectedSet.has(key);
          return (
            // Same behaviour as a clickable table row: the whole entry opens the row, by tap or by
            // Enter/Space when focused; controls inside it (checkbox, actions) are excluded in handleClick.
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <li
              key={key}
              data-state={selected ? "selected" : undefined}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? (e) => handleClick(e, row, index) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        onRowClick(row, index);
                      }
                    }
                  : undefined
              }
              className={cx(
                "p-3 transition-colors",
                selected && "bg-blue-50/60 dark:bg-blue-500/10",
                onRowClick && "cursor-pointer active:bg-app-table-header focus:outline-none focus-visible:bg-blue-50/60",
                typeof rowClassName === "function" ? rowClassName(row, index) : rowClassName,
              )}
            >
              <div className="flex items-start gap-3">
                {selectable && (
                  <span className="pt-0.5">
                    <DataTableCheckbox
                      checked={Boolean(selected)}
                      disabled={isRowSelectable ? !isRowSelectable(row) : false}
                      onChange={() => onToggleRow(key)}
                      aria-label={labels.selectRow(getRowAriaLabel ? getRowAriaLabel(row, index) : `row ${index + 1}`)}
                    />
                  </span>
                )}
                <div className="min-w-0 flex-1 break-words text-[15px] font-semibold text-app-heading">
                  {titleColumn && cell(titleColumn, row, index)}
                </div>
                {actions && <div className="-my-1 shrink-0">{cell(actions, row, index)}</div>}
              </div>
              {detailColumns.length > 0 && (
                <dl className={cx("mt-2 grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm", selectable && "pl-7")}>
                  {detailColumns.map((column) => (
                    <div key={column.key} className="contents">
                      <dt className="text-app-muted">{column.label}</dt>
                      <dd className="min-w-0 break-words text-right text-app-secondary">{cell(column, row, index)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
